// Story 9.6 (ADO-1080), AC10 — the labelled date FIELD that opens a {@link DatePicker}.
// Figma "Field · From date" 4925:50653 (the field) plus 4919:15617 (the panel in situ).
//
// The split is deliberate and follows the repo's `Popover` contract: `DatePicker` is a
// panel that knows about days, and this owns everything about being a form control —
// the label, the trigger, opening and closing, the outside click, and returning focus
// to the trigger afterwards. Anything that needs a calendar in a different chrome uses
// the panel directly.
//
// ── Why the trigger is a button and not a text input ──
// The frame draws a field with a placeholder and a calendar glyph, so it LOOKS like an
// input, and typing a date is a genuinely nice affordance. It is a button because a
// free-text date field is a promise this story cannot keep: `dd/mm/yyyy` typed as
// `2/1/26`, `02.01.2026` or `Jan 2` all have to be parsed or rejected, and a rejected
// value needs an error state the frame does not draw. A button opening a constrained
// calendar can only ever produce a real day, in bounds and (with `isDayEnabled`) on offer —
// which is the AC9 guarantee ("each picker constrains the other so the To date cannot
// precede the From date") stated as a control rather than as validation after the fact.
// The `error` state (Story 8.13, ADO-2022) is for a day the server refused, not for input.
//
// The clear affordance is the one addition to the frame: a chosen day needs a way back
// to "no bound", and the panel alone cannot express that (every day in it sets a day).
// It appears only when there is something to clear and the field is `clearable` (the
// default; Story 8.13's Fulfilment card turns it off, as a date is always required there).

import { useEffect, useId, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Icon } from '../Icon/Icon';
import { DatePicker } from './DatePicker';
import { formatDisplayDay, type IsoDay } from './calendar';
import styles from './DateField.module.css';

export interface DateFieldProps {
  /** The visible field label, e.g. "From date". */
  readonly label: string;
  /** The chosen day (`YYYY-MM-DD`), or null for an empty field. */
  readonly value: string | null;
  /** Called with the new day, or with null when the field is cleared. */
  readonly onChange: (day: IsoDay | null) => void;
  /** Shown when nothing is chosen. The frame's is "dd/mm/yyyy". */
  readonly placeholder?: string;
  /** Earliest selectable day, inclusive. */
  readonly min?: string;
  /** Latest selectable day, inclusive. */
  readonly max?: string;
  /** Story 8.13 (ADO-2022): which days inside the bounds may be picked; see `DatePicker`. */
  readonly isDayEnabled?: (day: string) => boolean;
  /** Story 8.13 (ADO-2022): an error message; when set the field renders its invalid state. */
  readonly error?: ReactNode;
  /** Story 8.13 (ADO-2022): whether a chosen day can be cleared with the × affordance. On by
   *  default (the Orders filter); the Fulfilment card turns it off, as its frames draw none. */
  readonly clearable?: boolean;
  /** The range being built, for the AC10 From/To highlight inside the panel. */
  readonly rangeStart?: string | null;
  readonly rangeEnd?: string | null;
  /** The viewer's today, which gets the ring. Injected so specs can pin it. */
  readonly today: string;
  /** The trigger's id. Generated when omitted. */
  readonly id?: string;
  /**
   * ADO-1779: hand the calendar to the caller instead of opening it under the field.
   * The mobile Filters sheet (Figma 5415:22466) shows the calendar as its own sheet
   * step, titled with this field's label, so there the trigger only asks for it.
   */
  readonly onOpenCalendar?: () => void;
}

export function DateField({
  label,
  value,
  onChange,
  placeholder = 'dd/mm/yyyy',
  min,
  max,
  isDayEnabled,
  rangeStart,
  rangeEnd,
  today,
  id,
  onOpenCalendar,
  error,
  clearable = true,
}: DateFieldProps) {
  const [open, setOpen] = useState(false);
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const panelId = `${fieldId}-calendar`;
  const errorId = error ? `${fieldId}-error` : undefined;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  /**
   * Set when the panel is closed by an action that should hand focus BACK to the
   * trigger (Escape, or picking a day) — not when it is closed by a click elsewhere on
   * the page, where pulling focus back would yank it away from whatever the user just
   * clicked.
   */
  const restoreFocusRef = useRef(false);

  const close = (restoreFocus: boolean) => {
    restoreFocusRef.current = restoreFocus;
    setOpen(false);
  };

  useEffect(() => {
    if (open || !restoreFocusRef.current) return;
    restoreFocusRef.current = false;
    triggerRef.current?.focus();
  }, [open]);

  // Outside click and Escape live on the DOCUMENT, because the panel is not modal:
  // focus can legitimately be anywhere when either happens. The trigger is excluded
  // from the outside-click test — it owns the toggle, and closing on its own mousedown
  // would be undone by its click handler reopening the panel a moment later (the
  // `Popover` lesson).
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (target === null) return;
      if (wrapperRef.current?.contains(target) === true) return;
      close(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close(true);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const hasValue = value !== null && value !== '';

  return (
    <div className={styles.field} ref={wrapperRef}>
      {/* A <label> for a <button>: <button> is a labelable element, so this associates
          natively and clicking the label opens the calendar, exactly as it would focus
          an input. Same treatment FormField gives its own label. */}
      <label className={styles.label} htmlFor={fieldId}>
        {label}
      </label>

      <div className={styles.control}>
        <button
          type="button"
          id={fieldId}
          ref={triggerRef}
          className={[styles.trigger, error ? styles.invalid : ''].filter(Boolean).join(' ')}
          // Story 8.13 (ADO-2022): the error is described and announced (`role="alert"`). Not
          // `aria-invalid`, which ARIA does not support on a button (the trigger is one, so it
          // can only produce a real day); `data-invalid` drives the error stroke.
          aria-describedby={errorId}
          data-invalid={error ? true : undefined}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-controls={open ? panelId : undefined}
          data-empty={!hasValue}
          onClick={() => {
            if (onOpenCalendar !== undefined) onOpenCalendar();
            else if (open) close(true);
            else setOpen(true);
          }}
        >
          <span className={styles.valueText}>
            {hasValue ? formatDisplayDay(value) : placeholder}
          </span>
          <Icon name="calendar" size={18} className={styles.calendarIcon} />
        </button>

        {hasValue && clearable ? (
          // Sits OUTSIDE the trigger button: a button inside a button is invalid HTML
          // and the inner one is unreachable by keyboard. Absolutely positioned over
          // the trigger's right edge, beside the glyph.
          <button
            type="button"
            className={styles.clear}
            aria-label={`Clear ${label.toLowerCase()}`}
            onClick={() => onChange(null)}
          >
            <Icon name="x" size={14} />
          </button>
        ) : null}

        {open ? (
          <div
            id={panelId}
            role="dialog"
            aria-label={label}
            aria-modal={false}
            className={styles.popup}
          >
            <DatePicker
              value={value}
              today={today}
              aria-label={label}
              onSelect={(day) => {
                onChange(day);
                // Choosing a day answers the question the panel was opened to ask, so
                // it closes — and focus goes back to the trigger, which now shows the
                // chosen date, rather than being lost with the removed panel.
                close(true);
              }}
              onDismiss={() => close(true)}
              {...(min === undefined ? {} : { min })}
              {...(max === undefined ? {} : { max })}
              {...(isDayEnabled === undefined ? {} : { isDayEnabled })}
              {...(rangeStart === undefined ? {} : { rangeStart })}
              {...(rangeEnd === undefined ? {} : { rangeEnd })}
            />
          </div>
        ) : null}
      </div>
      {error ? (
        <span id={errorId} className={styles.error} role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}
